import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { EyeIcon, UserIcon, PlusIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI, offerAPI, mutualisteAPI, enterpriseAPI, formatCurrency, formatDateTime, getStatusColor, getStatusLabel, getOfferTypeLabel } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SubscriptionDetailModal from '../components/Admin/SubscriptionDetailModal';
import CreateSubscriptionModal from '../components/Admin/CreateSubscriptionModal';

const AdminSubscriptions = () => {
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [gotoPage, setGotoPage] = useState('');
  
  const [filters, setFilters] = useState({
    product: '',
    status: '',
    mutualiste: '',
    enterprise: '',
    startDate: '',
    endDate: ''
  });
  
  const [mutualisteSearch, setMutualisteSearch] = useState('');
  const [mutualistes, setMutualistes] = useState([]);
  const [showMutualisteDropdown, setShowMutualisteDropdown] = useState(false);
  
  const [enterpriseSearch, setEnterpriseSearch] = useState('');
  const [enterprises, setEnterprises] = useState([]);
  const [showEnterpriseDropdown, setShowEnterpriseDropdown] = useState(false);
  
  // Refs pour détecter les clics en dehors
  const mutualisteRef = useRef(null);
  const enterpriseRef = useRef(null);

  const pageSize = 10;

  // Récupérer les souscriptions (toutes pour l'admin) avec pagination serveur
  const { data: subscriptionsData, isLoading } = useQuery(
    ['admin-subscriptions', filters, currentPage],
    () => subscriptionAPI.getAllSubscriptions({ 
      page: currentPage,
      limit: pageSize,
      status: filters.status || undefined,
      offerId: filters.product || undefined,
      mutualisteId: filters.mutualiste || undefined,
      enterpriseId: filters.enterprise || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined
    }),
    {
      refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
    }
  );

  // Récupérer les statistiques (filtrées)
  const { data: statsData } = useQuery(
    ['subscription-stats', filters],
    () => subscriptionAPI.getStats({
      status: filters.status || undefined,
      offerId: filters.product || undefined,
      mutualisteId: filters.mutualiste || undefined,
      enterpriseId: filters.enterprise || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined
    }),
    {
      refetchInterval: 60000, // Rafraîchir toutes les minutes
    }
  );

  const stats = statsData?.data || {};

  // Récupérer la liste des produits
  const { data: offersData } = useQuery(
    'offers-list',
    () => offerAPI.getOffers()
  );

  const offers = offersData?.data?.offers || offersData?.data || [];

  // Fermer les dropdowns en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mutualisteRef.current && !mutualisteRef.current.contains(event.target)) {
        setShowMutualisteDropdown(false);
      }
      if (enterpriseRef.current && !enterpriseRef.current.contains(event.target)) {
        setShowEnterpriseDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const subscriptions = subscriptionsData?.data?.subscriptions || subscriptionsData?.subscriptions || [];
  const pagination = subscriptionsData?.data?.pagination || subscriptionsData?.pagination || {};
  const totalPages = pagination.pages || Math.max(1, Math.ceil((pagination.total || subscriptions.length || 1) / pageSize));

  const handleViewDetails = async (subscription) => {
    try {
      const response = await subscriptionAPI.getSubscription(subscription.id);
      const detailed = response.data?.data?.subscription || response.data?.subscription || subscription;
      setSelectedSubscription(detailed);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement du détail de la souscription:', error);
      // En cas d'erreur, on affiche quand même l'objet existant
      setSelectedSubscription(subscription);
      setShowDetailModal(true);
    }
  };

  // Recherche de mutualistes
  const searchMutualistes = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setMutualistes([]);
      setShowMutualisteDropdown(false);
      return;
    }

    try {
      const response = await mutualisteAPI.searchMutualistes({ search: searchTerm });
      const mutualistesData = response.data?.data?.mutualistes || response.data?.mutualistes || [];
      setMutualistes(mutualistesData);
      setShowMutualisteDropdown(true);
    } catch (error) {
      console.error('Erreur recherche mutualistes:', error);
      setMutualistes([]);
    }
  };

  // Recherche d'entreprises
  const searchEnterprises = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setEnterprises([]);
      setShowEnterpriseDropdown(false);
      return;
    }

    try {
      const response = await enterpriseAPI.search({ q: searchTerm });
      const enterprisesData = response.data?.data || [];
      setEnterprises(enterprisesData);
      setShowEnterpriseDropdown(true);
    } catch (error) {
      console.error('Erreur recherche entreprises:', error);
      setEnterprises([]);
    }
  };

  // Handlers pour la sélection
  const handleSelectMutualiste = (mutualiste) => {
    setMutualisteSearch(`${mutualiste.matricule} - ${mutualiste.nom} ${mutualiste.prenoms}`);
    setFilters(prev => ({ ...prev, mutualiste: mutualiste.id }));
    setShowMutualisteDropdown(false);
  };

  const handleSelectEnterprise = (enterprise) => {
    setEnterpriseSearch(enterprise.nom);
    setFilters(prev => ({ ...prev, enterprise: enterprise.id }));
    setShowEnterpriseDropdown(false);
  };

  const getPaginationItems = (current, total, maxVisible = 9) => {
    const items = [];

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) items.push(i);
      return items;
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, current - half);
    let end = start + maxVisible - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) items.push(i);

    return items;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Gestion des souscriptions
            </h1>
            <p className="text-gray-600">
              Validez ou rejetez les demandes de souscription des mutualistes
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nouvelle souscription
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">
              <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-500 mb-1">Total</p>
            <p className="text-xl font-bold text-gray-900">{stats.total || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
              <svg className="h-4 w-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-500 mb-1">En attente</p>
            <p className="text-xl font-bold text-gray-900">{stats.enAttente || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center mb-2">
              <svg className="h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-500 mb-1">En traitement</p>
            <p className="text-xl font-bold text-gray-900">{stats.enTraitement || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mb-2">
              <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-500 mb-1">Validées</p>
            <p className="text-xl font-bold text-gray-900">{stats.validees || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center mb-2">
              <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-500 mb-1">Rejetées</p>
            <p className="text-xl font-bold text-gray-900">{stats.rejetees || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center mb-2">
              <svg className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <p className="text-xs font-medium text-gray-500 mb-1">Annulées</p>
            <p className="text-xl font-bold text-gray-900">{stats.annulees || 0}</p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Filtre Date début */}
          <div>
            <label className="form-label">Date début</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="form-input"
            />
          </div>

          {/* Filtre Date fin */}
          <div>
            <label className="form-label">Date fin</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="form-input"
            />
          </div>

          {/* Filtre Produit */}
          <div>
            <label className="form-label">Produit</label>
            <select
              value={filters.product}
              onChange={(e) => setFilters(prev => ({ ...prev, product: e.target.value }))}
              className="form-input"
            >
              <option value="">Tous les produits</option>
              {offers.map(offer => (
                <option key={offer.id} value={offer.id}>
                  {offer.name} - {getOfferTypeLabel(offer.type)}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre Statut */}
          <div>
            <label className="form-label">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="form-input"
            >
              <option value="">Tous les statuts</option>
              <option value="En attente">En attente</option>
              <option value="Validée">Validées</option>
              <option value="Rejetée">Rejetées</option>
              <option value="Annulée">Annulées</option>
              <option value="En traitement">En traitement</option>
            </select>
          </div>

          {/* Filtre Entreprise */}
          <div className="relative" ref={enterpriseRef}>
            <label className="form-label">Entreprise</label>
            <input
              type="text"
              value={enterpriseSearch}
              onChange={(e) => {
                const value = e.target.value;
                setEnterpriseSearch(value);
                if (!value) {
                  setFilters(prev => ({ ...prev, enterprise: '' }));
                } else {
                  searchEnterprises(value);
                }
              }}
              onFocus={() => enterpriseSearch.length >= 2 && setShowEnterpriseDropdown(true)}
              placeholder="Rechercher entreprise..."
              className="form-input"
            />
            {showEnterpriseDropdown && enterprises.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {enterprises.map((enterprise) => (
                  <div
                    key={enterprise.id}
                    onClick={() => handleSelectEnterprise(enterprise)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <div className="font-medium">{enterprise.nom}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filtre Mutualiste */}
          <div className="relative" ref={mutualisteRef}>
            <label className="form-label">Mutualiste</label>
            <input
              type="text"
              value={mutualisteSearch}
              onChange={(e) => {
                const value = e.target.value;
                setMutualisteSearch(value);
                if (!value) {
                  setFilters(prev => ({ ...prev, mutualiste: '' }));
                } else {
                  searchMutualistes(value);
                }
              }}
              onFocus={() => mutualisteSearch.length >= 2 && setShowMutualisteDropdown(true)}
              placeholder="Rechercher matricule ou nom..."
              className="form-input"
            />
            {showMutualisteDropdown && mutualistes.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {mutualistes.map((mutualiste) => (
                  <div
                    key={mutualiste.id}
                    onClick={() => handleSelectMutualiste(mutualiste)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <div className="font-medium">{mutualiste.matricule}</div>
                    <div className="text-sm text-gray-600">{mutualiste.nom} {mutualiste.prenoms}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Liste des souscriptions */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {subscriptions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucune souscription trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mutualiste
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bénéficiaires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de demande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {subscription.mutualiste?.nom} {subscription.mutualiste?.prenoms}
                          </div>
                          <div className="text-sm text-gray-500">
                            Matricule: {subscription.mutualiste?.matricule}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {subscription.offer?.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getOfferTypeLabel(subscription.offer?.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(subscription.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {subscription.beneficiariesCount || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(subscription.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge badge-${getStatusColor(subscription.status?.libelle || subscription.status)}`}>
                        {getStatusLabel(subscription.status?.libelle || subscription.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <button
                        onClick={() => handleViewDetails(subscription)}
                        className="inline-flex items-center justify-center text-blue-600 hover:text-blue-900"
                        title="Voir les détails"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {subscriptions.length > 0 && (
        <div className="bg-white rounded-xl shadow-card px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Précédent
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center space-x-4">
              <p className="text-sm text-gray-700">
                Page <span className="font-medium">{currentPage}</span> sur{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={gotoPage}
                  onChange={(e) => setGotoPage(e.target.value)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Aller à"
                />
                <button
                  type="button"
                  onClick={() => {
                    const pageNumber = parseInt(gotoPage, 10);
                    if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
                      setCurrentPage(pageNumber);
                    }
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Aller
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
              >
                Préc.
              </button>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                {getPaginationItems(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </nav>
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 rounded-md"
              >
                Suiv.
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détail de souscription */}
      <SubscriptionDetailModal
        subscription={selectedSubscription}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedSubscription(null);
        }}
      />

      {/* Modal de création de souscription */}
      <CreateSubscriptionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
};

export default AdminSubscriptions;
